'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useTranslations } from 'gt-react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, Printer, X } from 'lucide-react'
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { clampIndex } from '@/components/ui/image-lightbox.utils'
import { ImageZoomPan } from '@/components/ui/image-zoom-pan'

// Print alleen de afbeelding zelf — niet de dialog of de pagina erachter. Een
// verborgen iframe met enkel de <img>; printen zodra de afbeelding geladen is.
function printImageUrl(url: string) {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(iframe)
  const win = iframe.contentWindow
  const doc = iframe.contentDocument
  if (!win || !doc) {
    iframe.remove()
    return
  }
  const cleanup = () => iframe.remove()
  win.onafterprint = cleanup
  const style = doc.createElement('style')
  style.textContent = '@page{margin:10mm}body{margin:0}img{max-width:100%}'
  doc.head.appendChild(style)
  const img = doc.createElement('img')
  img.onload = () => {
    win.focus()
    win.print()
  }
  img.onerror = cleanup
  img.src = url
  doc.body.appendChild(img)
}

export type LightboxImage = {
  /** Signed inline GET-URL (wat de thumbnail al toont). */
  url: string
  /** Bijschrift, bv. "Logo vignet (donker)". */
  label?: string
  alt?: string
  /** Lazy signed attachment-URL; aanwezig ⇒ Download-knop. */
  getDownloadUrl?: () => Promise<string | null>
}

type OpenFn = (images: LightboxImage[], index?: number) => void
const LightboxContext = createContext<OpenFn | null>(null)

export function useImageLightbox(): { open: OpenFn } {
  const open = useContext(LightboxContext)
  if (!open) throw new Error('useImageLightbox must be used within <ImageLightboxProvider>')
  return { open }
}

/** Eén instantie aan de app-root (binnen de i18n-provider). */
export function ImageLightboxProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<LightboxImage[]>([])
  const [index, setIndex] = useState(0)
  const [open, setOpen] = useState(false)

  const openFn = useCallback<OpenFn>((imgs, i = 0) => {
    if (!imgs.length) return
    setImages(imgs)
    setIndex(clampIndex(i, imgs.length))
    setOpen(true)
  }, [])

  return (
    <LightboxContext.Provider value={openFn}>
      {children}
      <ImageLightbox
        images={images}
        index={index}
        open={open}
        onIndexChange={(i) => setIndex(clampIndex(i, images.length))}
        onOpenChange={setOpen}
      />
    </LightboxContext.Provider>
  )
}

function ImageLightbox({
  images,
  index,
  open,
  onIndexChange,
  onOpenChange,
}: {
  images: LightboxImage[]
  index: number
  open: boolean
  onIndexChange: (i: number) => void
  onOpenChange: (o: boolean) => void
}) {
  const t = useTranslations()
  const current = images[index]
  const many = images.length > 1

  const download = async () => {
    if (!current?.getDownloadUrl) return
    try {
      const url = await current.getDownloadUrl()
      if (!url) {
        toast.error(t('common.image.downloadFailed'))
        return
      }
      // Cross-origin attachment-disposition forceert de download; de filename komt
      // uit de signed URL (server-side gezet). Een tijdelijke anchor triggert 'm.
      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch {
      toast.error(t('common.image.downloadFailed'))
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          // Inset positioning (NO translate-centering). The shadcn Dialog centres with
          // translate(-50%,-50%), which composites the panel on a half-pixel and softens
          // the image; inset keeps it on the pixel grid → crisp (same fix as the PDF viewer).
          className="fixed inset-3 z-50 block overflow-hidden rounded-lg border bg-background shadow-lg focus:outline-none"
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (!many) return
            if (e.key === 'ArrowLeft') onIndexChange(index - 1)
            if (e.key === 'ArrowRight') onIndexChange(index + 1)
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {current?.label ?? current?.alt ?? t('common.image.close')}
          </DialogPrimitive.Title>
          {current && (
            <div className="relative h-full w-full bg-muted/20">
              <ImageZoomPan
                src={current.url}
                alt={current.alt ?? current.label ?? ''}
                allowUpscale={false}
              />

              {many && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="absolute left-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
                    onClick={() => onIndexChange(index - 1)}
                    disabled={index === 0}
                    title={t('common.image.previous')}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
                    onClick={() => onIndexChange(index + 1)}
                    disabled={index === images.length - 1}
                    title={t('common.image.next')}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </>
              )}

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-background/80 p-2 backdrop-blur">
                <span className="truncate text-sm text-muted-foreground">
                  {current.label ?? ''}
                  {many ? ` (${index + 1}/${images.length})` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => printImageUrl(current.url)}
                  >
                    <Printer className="mr-1 h-4 w-4" />
                    {t('common.image.print')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(current.url, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="mr-1 h-4 w-4" />
                    {t('common.image.openInNewTab')}
                  </Button>
                  {current.getDownloadUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void download()}
                    >
                      <Download className="mr-1 h-4 w-4" />
                      {t('common.image.download')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogPrimitive.Close
            className="absolute end-3 top-3 z-10 cursor-pointer rounded-sm bg-background/70 p-1 opacity-80 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={t('common.image.close')}
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
